import { useCallback } from 'react';
import { FormikErrors, FormikHelpers, FormikState, useFormikContext } from 'formik';
import {
  FieldPermission,
  FieldValidatorInterface,
  FormControllerFieldMeta,
  SmartformContextProps,
  SmartformTranslations,
  useSmartformContext
} from '../controllers';
import { restoreSanitizedFieldName } from './model/fieldNameSanitizer';
import { objectUtil } from './objectUtil';
import { CustomFieldDefinitionEntityTypeModel, FieldKeyModel } from '@utmost.co/enterprise-models';
import { SmartFormFeatureFlags } from '../SmartFormFeatures.types';

const META_PATH = '$meta';
export function getMetaPathInSchemaFor(itemName: string) {
  return `$${META_PATH}.${itemName}`;
}
function getMetaPathFor(itemName: string) {
  return `${META_PATH}.${itemName}`;
}

interface SmartFormContext<Values = unknown, Model = unknown> {
  setFieldValue: FormikHelpers<Values>['setFieldValue'];
  values: Values;
  setFieldError: FormikHelpers<Values>['setFieldError'];
  setFieldValues: (valuesToSet: { [p: string]: any }, shouldValidate?: boolean) => void;
  setFieldTouched: FormikHelpers<Values>['setFieldTouched'];
  resetForm: FormikHelpers<Values>['resetForm'];
  previewMode: boolean | undefined;
  permissionDebugger: boolean | undefined;
  isValidating: FormikState<Values>['isValidating'];
  initialValues: Values;
  getError: (name: string) => ConditionalError<Model> | undefined;
  getValue: (name: string) => Model | undefined;
  getDisabled: (name: string) => boolean;
  getFieldPermissions: (name: string) => FieldPermission | undefined;
  submitForm: FormikHelpers<Values>['submitForm'];
  readonly: boolean | undefined;
  targetEntityUid: string | undefined;
  requestHeaders?: Record<string, string>;
  parentEntityUids?: Map<CustomFieldDefinitionEntityTypeModel, string>;
  translations?: SmartformTranslations;
  validator?: FieldValidatorInterface;
  contextMap?: Map<string, object>;
  fieldMeta: FormControllerFieldMeta | undefined;
  errors: FormikErrors<Values>;
  submitCount: number;
  isSubmitting: FormikState<Values>['isSubmitting'];
  validateForm: FormikHelpers<Values>['validateForm'];
  featureFlags?: SmartFormFeatureFlags;
  setMetaValue: (itemName: string, itemValue: any) => void;
  getMetaValue: (itemName: string) => unknown;
  onFieldBlur?: (fieldKey: FieldKeyModel, value: any, path: string) => void;
}

type ConditionalError<T extends any[] | any> = T extends any[] ? string | string[] : string;

interface FieldSmartFormContext<Model = unknown, Values = unknown> extends SmartFormContext<Values, Model> {
  field: {
    setTouched: (shouldValidate?: boolean) => void;
    setValue: (value: Model | undefined | null) => void;
    setError: (message: string) => void;
    getPermissions: () => ReturnType<SmartFormContext<Values, Model>['getFieldPermissions']>;
    disabled: boolean;
    error: ConditionalError<Model> | undefined;
    value: Model | undefined;
  };
}

/**
 * This could arguably be called use smartform state or internals rather than context. It itself accesses two
 * contexts, but it does not grant direct access to them.
 */
export function useSmartFormContext<Values = unknown, Model = unknown>(): SmartFormContext<Values, Model> {
  const {
    submitForm,
    isSubmitting,
    isValidating,
    setFieldValue,
    values,
    setFieldError,
    resetForm,
    errors,
    setFieldTouched,
    initialValues,
    touched,
    submitCount,
    validateForm
  } = useFormikContext<Values>();
  const {
    readonly,
    fieldMeta,
    validator,
    previewMode,
    contextMap,
    targetEntityUid,
    requestHeaders,
    parentEntityUids,
    permissionDebugger,
    translations,
    featureFlags,
    onFieldBlur
  }: Partial<SmartformContextProps> = useSmartformContext();

  const setFieldValues = useCallback(
    (valuesToSet: { [prop: string]: any }, shouldValidate?: boolean) => {
      Object.entries(valuesToSet).forEach(([key, value]) => {
        setFieldValue(key, value, shouldValidate);
      });
    },
    [setFieldValue]
  );

  const setMetaValue = useCallback(
    (itemName: string, itemValue: any) => {
      setFieldValue(getMetaPathFor(itemName), itemValue, true);
    },
    [setFieldValue]
  );

  const getMetaValue = (itemName: string) => {
    return getter(getMetaPathFor(itemName), values as Record<string, any>);
  };

  function getValue<T = string>(name: string): T | undefined {
    return getter<T>(name, values as Record<string, any>);
  }

  function getError(name: string): ConditionalError<Model> | undefined {
    const isTouched = submitCount > 0 || !!getter(name, touched);
    return isTouched ? getter<ConditionalError<Model>>(name, errors) : undefined;
  }

  const getDisabled = useCallback(
    (name: string) => {
      const restoredName = restoreSanitizedFieldName(name);
      const metaData = fieldMeta?.[restoredName];

      if (isSubmitting) return true;

      return metaData?.disabled || false;
    },
    [fieldMeta, isSubmitting]
  );

  const getFieldPermissions = useCallback(
    (name: string) => {
      let restoredName = restoreSanitizedFieldName(name);

      // Sometimes names come in using an array index. We need to remove that. Only the first part is correct
      if (restoredName.includes('[')) restoredName = restoredName.split('[')[0]!;
      const metaData = fieldMeta?.[restoredName];
      if (!metaData && restoredName.includes('customFields.CF_'))
        return { READ: false, PERMISSION_NAME: restoredName as any };

      return metaData?.permissions;
    },
    [fieldMeta]
  );

  return {
    submitForm,
    isSubmitting,
    isValidating,
    setFieldValue,
    values,
    setFieldError,
    resetForm,
    errors,
    translations,
    setFieldValues,
    setFieldTouched,
    initialValues,
    readonly,
    fieldMeta,
    validator,
    contextMap,
    submitCount,
    previewMode,
    permissionDebugger,
    getValue,
    getError,
    getDisabled,
    getFieldPermissions,
    validateForm,
    targetEntityUid,
    requestHeaders,
    parentEntityUids,
    featureFlags,
    setMetaValue,
    getMetaValue,
    onFieldBlur
  };
}

export function useFieldSmartFormContext<Model = string, Values = unknown>(
  fieldName: string
): FieldSmartFormContext<Model, Values> {
  const context = useSmartFormContext<Values, Model>();
  return {
    ...context,
    field: {
      value: context.getValue(fieldName),
      error: context.getError(fieldName),
      disabled: context.getDisabled(fieldName),
      getPermissions: () => context.getFieldPermissions(fieldName),
      setValue: (value: Model | undefined | null) => context.setFieldValue(fieldName, value),
      setTouched: (shouldValidate?: boolean) => context.setFieldTouched(fieldName, true, shouldValidate),
      setError: (message: string) => context.setFieldError(fieldName, message)
    }
  };
}

function getter<T>(name: string, obj: Record<string, any>): T | undefined {
  return objectUtil.getIn(obj, name) as T | undefined;
}
